import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Check, Save, AlertCircle, HelpCircle } from "lucide-react";
import {
  createCheck,
  getCheck,
  updateCheck,
  validateCheck,
  type CheckPayload,
} from "@/api/checks";
import { listActions } from "@/api/actions";
import { ApiError } from "@/api/client";
import { Page } from "@/components/page";
import { JsonEditor } from "@/components/json-editor";
import { VerificationCorpusPanel } from "@/components/verification-corpus-panel";
import { AiImprovePanel } from "@/components/ai-improve-panel";
import type { ProposedCheck } from "@/api/authoring";
import { useDebounced } from "@/lib/use-debounce";

const EMPTY: CheckPayload = {
  name: "",
  priority: 100,
  action_id: "",
  enabled: true,
  shop_system_label: null,
  llm_verification_prompt: null,
  brief: null,
  kind: "content",
  positive_rules: [],
  negative_rules: [],
};

export function CheckEditPage() {
  const { checkId } = useParams({ strict: false }) as { checkId: string };
  const isNew = checkId === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const remote = useQuery({
    queryKey: ["check", checkId],
    queryFn: () => getCheck(checkId),
    enabled: !isNew,
  });
  const actionsQuery = useQuery({
    queryKey: ["actions"],
    queryFn: listActions,
  });
  const enabledActions = useMemo(
    () => (actionsQuery.data ?? []).filter((a) => a.enabled),
    [actionsQuery.data],
  );

  const [draft, setDraft] = useState<CheckPayload>(EMPTY);

  // Default new checks to the first enabled action so the form is valid
  // by the time the user clicks Save without explicit picking.
  useEffect(() => {
    if (!isNew) return;
    if (draft.action_id || enabledActions.length === 0) return;
    setDraft((d) => ({ ...d, action_id: enabledActions[0].id }));
  }, [isNew, enabledActions, draft.action_id]);
  const [posJson, setPosJson] = useState("[]");
  const [negJson, setNegJson] = useState("[]");
  const [tab, setTab] = useState<"positive" | "negative">("positive");
  const [parseError, setParseError] = useState<string | null>(null);

  // Hydrate form from server fetch.
  useEffect(() => {
    if (!remote.data) return;
    setDraft({
      name: remote.data.name,
      priority: remote.data.priority,
      action_id: remote.data.action.id,
      enabled: remote.data.enabled,
      shop_system_label: remote.data.shop_system_label,
      llm_verification_prompt: remote.data.llm_verification_prompt,
      brief: remote.data.brief,
      kind: remote.data.kind,
      positive_rules: remote.data.positive_rules,
      negative_rules: remote.data.negative_rules,
    });
    setPosJson(JSON.stringify(remote.data.positive_rules, null, 2));
    setNegJson(JSON.stringify(remote.data.negative_rules, null, 2));
  }, [remote.data]);

  // Parse the JSON editors into the draft. Any parse error blocks save.
  useEffect(() => {
    try {
      const pos = JSON.parse(posJson || "[]");
      const neg = JSON.parse(negJson || "[]");
      if (!Array.isArray(pos) || !Array.isArray(neg)) {
        throw new Error("positive_rules and negative_rules must be JSON arrays");
      }
      setDraft((d) => ({ ...d, positive_rules: pos, negative_rules: neg }));
      setParseError(null);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  }, [posJson, negJson]);

  const debouncedDraft = useDebounced(draft, 350);
  const validation = useQuery({
    queryKey: ["check", "validate", debouncedDraft],
    queryFn: async () => {
      try {
        return await validateCheck(debouncedDraft);
      } catch (e) {
        if (e instanceof ApiError) return e;
        throw e;
      }
    },
    enabled: !!debouncedDraft.name && !parseError,
    staleTime: 1_000,
    retry: false,
  });

  const validationError = useMemo(() => {
    const v = validation.data;
    if (!v) return null;
    if (v instanceof ApiError) return formatApiError(v);
    return null;
  }, [validation.data]);

  const save = useMutation({
    mutationFn: () =>
      isNew ? createCheck(draft) : updateCheck(checkId, draft),
    onSuccess: (r) => {
      toast.success(isNew ? "Check created" : "Check saved");
      qc.invalidateQueries({ queryKey: ["checks"] });
      qc.invalidateQueries({ queryKey: ["check", checkId] });
      if (isNew && "id" in r) {
        navigate({ to: "/checks/$checkId", params: { checkId: r.id as string } });
      }
    },
  });

  const canSave =
    !parseError && !validationError && !!draft.name && !save.isPending;

  // Load an AI-proposed revision into the editor (rules + tunable fields).
  // Not saved — the operator reviews, then Saves through the normal flow.
  // The name is left untouched so a deliberate rename isn't clobbered.
  const applyProposed = (p: ProposedCheck) => {
    setPosJson(JSON.stringify(p.positive_rules, null, 2));
    setNegJson(JSON.stringify(p.negative_rules, null, 2));
    setDraft((d) => ({
      ...d,
      priority: p.priority,
      shop_system_label: p.shop_system_label,
      llm_verification_prompt: p.llm_verification_prompt,
    }));
    toast.success("Suggestion applied — review and Save");
  };

  return (
    <Page
      title={isNew ? "New check" : remote.data?.name ?? "Check"}
      actions={
        <>
          <Link to="/checks" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> All checks
          </Link>
          <button
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={!canSave}
          >
            <Save className="h-4 w-4" /> Save
          </button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[24rem_1fr]">
        <section className="card p-4 space-y-3">
          <h2 className="text-sm font-semibold">Settings</h2>
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label">Brief (what this check finds)</label>
            <textarea
              className="input min-h-[4.5rem] text-xs"
              value={draft.brief ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, brief: e.target.value || null })
              }
              placeholder="e.g. German Altenheim / senior-care homes; or Shopware 6 shops"
            />
            <p className="mt-1 text-xs text-muted leading-relaxed">
              A plain-language description of the kind of site this check should
              match. Read by the <em>Improve with AI</em> panel below; the engine
              ignores it.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Priority</label>
              <input
                type="number"
                className="input"
                value={draft.priority}
                onChange={(e) =>
                  setDraft({ ...draft, priority: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div>
              <label className="label">Action</label>
              <select
                className="input"
                value={draft.action_id}
                onChange={(e) => setDraft({ ...draft, action_id: e.target.value })}
              >
                {enabledActions.length === 0 ? (
                  <option value="">(no actions configured)</option>
                ) : null}
                {enabledActions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.spec.kind})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted">
                Manage actions on{" "}
                <Link to="/actions" className="text-accent hover:underline">
                  /actions
                </Link>
                .
              </p>
            </div>
          </div>
          <div>
            <label className="label">AI authoring agent</label>
            <select
              className="input"
              value={draft.kind ?? "content"}
              onChange={(e) =>
                setDraft({ ...draft, kind: e.target.value as "content" | "tech" })
              }
            >
              <option value="content">Content (word-index categories)</option>
              <option value="tech">Tech (CMS / shop fingerprints)</option>
            </select>
            <p className="mt-1 text-xs text-muted leading-relaxed">
              Picks which "Improve with AI" agent tunes this check.{" "}
              <strong>Content</strong> reasons over rendered-text words
              (Steuerberater, Altenheim). <strong>Tech</strong> probes markup,
              paths and headers for a CMS/shop fingerprint (Shopware, JTL). The
              detection engine ignores this — it only steers the AI agent.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <div>
            <label className="label">Shop-system label (optional)</label>
            <input
              className="input"
              value={draft.shop_system_label ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, shop_system_label: e.target.value || null })
              }
              placeholder="defaults to Name"
            />
            <p className="mt-1 text-xs text-muted leading-relaxed">
              Sent to Close as the shop-system custom field when this check is
              the primary match on an extract-kind action. Leave empty to fall
              back to the check Name. Use this when the tag and the shop-system
              label should differ — e.g. a check named{" "}
              <code>Altenheim</code> reporting as <code>Wordpress</code>.
            </p>
          </div>
          <div>
            <label className="label">LLM verification prompt (optional)</label>
            <textarea
              className="input min-h-[6rem] font-mono text-xs"
              value={draft.llm_verification_prompt ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  llm_verification_prompt: e.target.value || null,
                })
              }
              placeholder="When set, GPT-5-mini double-checks primary EXTRACT matches."
            />
            <div className="mt-1 space-y-2 text-xs text-muted leading-relaxed">
              <p>
                <strong>When:</strong> optional secondary filter. Used only
                when this check is the primary match on an extract-kind
                action. GPT-5-mini receives this text as a <em>system</em>{" "}
                message and the cleaned page body (HTML stripped, capped at
                6000 chars) as the user message. On <strong>no</strong>,
                this match is dropped and the next-priority match is
                re-elected as primary; if none remain the domain is
                skipped. Fails open on GPT errors. Skip for clear-cut
                rule-based checks; use for fuzzy categories where positive
                rules over-fire (incidental mentions, look-alike
                businesses, ambiguous language).
              </p>
              <p>
                <strong>How:</strong> describe what the check is{" "}
                <em>trying to identify</em> in enough detail to discriminate
                look-alikes — name the target category, list the
                disqualifiers, give one or two example edge cases. The
                response schema is fixed (
                <code>{`{ matches: bool, reason: string }`}</code>, enforced
                server-side via structured output), so do <em>not</em> ask for
                JSON, fields, or a specific format — just describe the
                classification. Reasoning is set to <code>minimal</code> and
                verbosity to <code>low</code>: the model returns a one-sentence
                reason and a verdict, no chain-of-thought, so be explicit about
                your criteria rather than relying on the model to infer them.
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-3 min-w-0">
          <RuleDocsPanel />
          <ValidationBanner error={parseError ?? validationError} ok={!parseError && !validationError && !!draft.name} />
          <div className="card overflow-hidden">
            <div className="px-4 py-2 border-b border-border flex items-center gap-1 text-sm">
              {(["positive", "negative"] as const).map((t) => (
                <button
                  key={t}
                  className={
                    "px-2.5 py-1 rounded-md text-xs font-medium " +
                    (tab === t
                      ? "bg-accent/15 text-accent"
                      : "text-muted hover:bg-subtle")
                  }
                  onClick={() => setTab(t)}
                >
                  {t === "positive"
                    ? `Positive rules (${draft.positive_rules.length})`
                    : `Negative rules (${draft.negative_rules.length})`}
                </button>
              ))}
            </div>
            <div className="p-3">
              {tab === "positive" ? (
                <JsonEditor value={posJson} onChange={setPosJson} height="360px" />
              ) : (
                <JsonEditor value={negJson} onChange={setNegJson} height="360px" />
              )}
            </div>
          </div>
          {!isNew && remote.data ? (
            <>
              <AiImprovePanel
                checkId={checkId}
                brief={draft.brief ?? null}
                workingPositive={draft.positive_rules}
                workingNegative={draft.negative_rules}
                onApply={applyProposed}
              />
              <VerificationCorpusPanel checkId={checkId} />
            </>
          ) : null}
        </section>
      </div>
    </Page>
  );
}

function ValidationBanner({
  error,
  ok,
}: {
  error: string | null;
  ok: boolean;
}) {
  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <pre className="text-xs whitespace-pre-wrap break-words font-mono">{error}</pre>
      </div>
    );
  }
  if (!ok) return null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/10 p-2 text-xs text-success">
      <Check className="h-4 w-4" /> Validated.
    </div>
  );
}

function RuleDocsPanel() {
  return (
    <details className="card p-4">
      <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-muted" />
        How checks work
      </summary>
      <div className="mt-3 space-y-4 text-sm leading-relaxed">
        <section className="space-y-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
            Evaluation order
          </h3>
          <p>
            Every enabled check is evaluated against every domain. Matches are
            sorted by <code>priority</code> ascending (lower number = higher
            precedence; ties broken by Name). The first match becomes the{" "}
            <em>primary</em>; all match names become tags on the domain; the
            primary's referenced <em>action</em> (managed on{" "}
            <Link to="/actions" className="text-accent hover:underline">
              /actions
            </Link>
            ) decides what happens next. Actions come in two kinds:
          </p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>
              <code>skip</code> — domain is tagged but no Close push (use this
              for blocklist-style checks, e.g. <code>bad words</code>).
            </li>
            <li>
              <code>extract</code> — fetch the Impressum, push to Close. Each
              extract action carries its own Close opportunity{" "}
              <code>status_id</code> so different domain flavours land in
              different pipelines.
            </li>
          </ul>
        </section>

        <section className="space-y-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
            Positive vs. negative rules
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Negative rules</strong> are an AND-of-NOTs gate: if{" "}
              <em>any</em> one matches, the whole check is disqualified for
              this domain. Use them to suppress false positives.
            </li>
            <li>
              <strong>Positive rules</strong> use first-match-wins, evaluated{" "}
              cheap-first then expensive (network-fetching). Cheap kinds:{" "}
              <code>html_contains</code>, <code>html_regex</code>,{" "}
              <code>html_all_of_regex</code>,{" "}
              <code>meta_generator_contains</code>, <code>header_equals</code>.
              Expensive kinds: <code>path_contains</code>,{" "}
              <code>path_regex</code>.
            </li>
            <li>
              Special case: when the first cheap match is{" "}
              <code>html_regex</code>, evaluation continues across the
              remaining <code>html_regex</code> rules to accumulate per-pattern
              hit counts (powers the <code>bad_content_hits</code> evidence on
              the seeded <code>bad words</code> check).
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
            Rule type reference
          </h3>
          <dl className="space-y-2.5">
            <RuleDoc
              shape={`{ "kind": "html_contains", "value": "..." }`}
              desc="Substring search in the saved HTML body."
            />
            <RuleDoc
              shape={`{ "kind": "html_regex", "pattern": "...", "min_count": 1 }`}
              desc="Python regex; matches when total hits ≥ min_count."
            />
            <RuleDoc
              shape={`{ "kind": "html_all_of_regex", "patterns": ["a", "b"], "min_count_each": 1, "min_counts": [] }`}
              desc="AND across patterns; each must hit its threshold. min_counts (one int per pattern) overrides min_count_each when non-empty."
            />
            <RuleDoc
              shape={`{ "kind": "meta_generator_contains", "value": "..." }`}
              desc={`Substring search within <meta name="generator" content="...">.`}
            />
            <RuleDoc
              shape={`{ "kind": "header_equals", "name": "Server", "value": "nginx" }`}
              desc="Exact HTTP response header match."
            />
            <RuleDoc
              shape={`{ "kind": "path_contains", "path": "admin", "value": "..." }`}
              desc="Fetch path (relative to the domain) and substring-search the body. Network call."
            />
            <RuleDoc
              shape={`{ "kind": "path_regex", "path": "...", "pattern": "...", "min_count": 1 }`}
              desc="Fetch path and regex-match the body. Network call."
            />
          </dl>
          <p className="text-xs text-muted">
            Order positive rules cheap → expensive within each list; the
            engine already does this internally, but writing them in
            evaluation order makes evidence easier to reason about.
          </p>
        </section>
      </div>
    </details>
  );
}

function RuleDoc({ shape, desc }: { shape: string; desc: string }) {
  return (
    <div>
      <pre className="p-2 rounded-md bg-subtle text-xs font-mono overflow-auto whitespace-pre-wrap break-words">
        {shape}
      </pre>
      <dd className="mt-1 text-xs text-muted">{desc}</dd>
    </div>
  );
}

function formatApiError(e: ApiError): string {
  if (Array.isArray(e.detail)) {
    return e.detail
      .map((d: any) => `${(d.loc ?? []).join(".")}: ${d.msg}`)
      .join("\n");
  }
  return typeof e.detail === "string" ? e.detail : e.message;
}
